import {
  type EmailChangeDraft,
  type EmailChangeValidationErrors,
  emptyDraft,
  type LoadStatus,
  type PendingEmailChange,
  type SubmitStatus,
} from '../domain/email-change.model';
import type { EmailChangeError } from '../domain/email-change-error';

/**
 * Redux state for the email-change section.
 *
 * `open` lives here rather than in component state for the same reason as in
 * `password-change.state.ts`: the section's shapes are one state machine with the
 * submit, and the saga is what knows it succeeded. Splitting them would mean the
 * saga reaching into a component.
 *
 * `pending` is the section's real subject. It is loaded on mount, so a reload still
 * shows what is in flight; which is also the only way someone can notice a request
 * they never made, while the current address still works.
 *
 * `cancelling` is separate from `submitStatus`: cancelling is not the reverse of
 * submitting, it is its own request, and conflating them would disable the wrong
 * button.
 */
export interface EmailChangeState {
  open: boolean;
  draft: EmailChangeDraft;
  submitStatus: SubmitStatus;
  validationErrors: EmailChangeValidationErrors;
  error: EmailChangeError | null;
  pending: PendingEmailChange | null;
  loadStatus: LoadStatus;
  cancelling: boolean;
}

export const initialState: EmailChangeState = {
  open: false,
  draft: emptyDraft,
  submitStatus: 'idle',
  validationErrors: {},
  error: null,
  pending: null,
  loadStatus: 'idle',
  cancelling: false,
};
